/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { AppSettings } from '../app.settings';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { LocalStorageCipherService } from './local-storage-cipher.service';

@Injectable()
export class StorageService {
    public logout: Subject<any> = new Subject<any>();
    cookiesItems = [];
    private enc2RewriteScheduled = new Set<string>();
    private memoryItems: Record<string, string> = {};
    private readonly memoryKeys = new Set<string>([
        AppSettings.USER_DETAILS_LOGIN_DATA,
        'permissionArray',
        'aiPermissionArray',
        AppSettings.USER_DETAILS_PROFILE_TIMEZONE,
        AppSettings.INTERVAL,
    ]);

    constructor(
        private router: Router,
        private localStorageCipher: LocalStorageCipherService,
    ) { }

    private shouldCacheInMemory(key: string, cacheInMemory = false): boolean {
        return cacheInMemory || this.memoryKeys.has(key);
    }

    private readFromMemory(key: string): string | undefined {
        if (Object.prototype.hasOwnProperty.call(this.memoryItems, key)) {
            return this.memoryItems[key];
        }
        return undefined;
    }

    private writeToMemory(key: string, plaintext: string): void {
        this.memoryItems[key] = plaintext;
    }

    private deleteFromMemory(key: string): void {
        delete this.memoryItems[key];
    }

  checkValidSession() {
    if (sessionStorage.getItem('validCustomerUser') !== 'validCustomer') {
      this.router.navigate(['/search']);
    }
  }

    getItem<T>(key: string): T {
        const cached = this.readFromMemory(key);
        if (cached !== undefined) {
            return cached as T;
        }

        const result = localStorage.getItem(key);

        let resultJson = null;
        if (result != null) {
            if (this.localStorageCipher.isEnc2Payload(result)) {
                const inner = this.localStorageCipher.open(result);
                if (inner != null && inner !== '') {
                    resultJson = inner;
                    this.rewriteEnc2KeyToPlaintextIfUnchanged(key, result, inner);
                } else {
                    resultJson = result;
                }
            } else {
                resultJson = result;
            }
        }
        if (resultJson != null && this.memoryKeys.has(key)) {
            this.writeToMemory(key, resultJson);
        }
        return resultJson as T;
    }

  /**
   * After a successful enc2 decrypt: return already happened in getItem.
   * Next tick, re-read the key (string compare only, no decrypt) and write plaintext
   * if it is still the same enc2 value. Comment out the call in getItem to skip this.
   */
  private rewriteEnc2KeyToPlaintextIfUnchanged(key: string, originalEnc2: string, plaintext: string): void {
    if (this.enc2RewriteScheduled.has(key)) {
      return;
    }
    this.enc2RewriteScheduled.add(key);
    setTimeout(() => {
      try {
        if (localStorage.getItem(key) === originalEnc2) {
          localStorage.setItem(key, plaintext);
        }
      } catch {
        // Quota or private mode: decrypted value was already returned to the caller.
      } finally {
        this.enc2RewriteScheduled.delete(key);
      }
    }, 0);
  }

    setItem<T>(key: string, value: T, cacheInMemory = false) {
        const plaintext = JSON.stringify(value);
        if (this.shouldCacheInMemory(key, cacheInMemory)) {
            this.writeToMemory(key, plaintext);
        }
        let payload = plaintext;
        if (this.localStorageCipher.isEnabled()) {
            payload = this.localStorageCipher.seal(payload);
        }
        localStorage.setItem(key, payload);
    }

    setItemAsIs(key: string, value) {
        let s = typeof value === 'string' ? value : String(value);
        if (this.shouldCacheInMemory(key)) {
            this.writeToMemory(key, s);
        }
        if (this.localStorageCipher.isEnabled()) {
            s = this.localStorageCipher.seal(s);
        }
        localStorage.setItem(key, s);
    }

  setItemInCookies(key: string, value: string) {
    this.cookiesItems.push(key);
    const d = new Date();
    d.setTime(d.getTime() + AppSettings.COOKIE_EXPIRY);
    const expires = 'expires=' + d.toUTCString();
    document.cookie = key + '=' + value + ';' + expires + ';path=/';
  }

  getItemFromCookies(key: string) {
    const name = key + '=';
    const ca = document.cookie.split(';');
    for (const cookie of ca) {
      let c = cookie;
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

    removeItem(key: string) {
        this.deleteFromMemory(key);
        localStorage.removeItem(key);
    }

    removeAll() {
        this.memoryItems = {};
        localStorage.clear();
    }

  clearAllUserStorage() {
    this.removeAll();
    sessionStorage.clear();
    this.removeAllCookies();
  }


  removeAllCookies() {
    this.cookiesItems.forEach(key => {
      this.setItemInCookies(key, '');
    });
    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    const cookies = document.cookie ? document.cookie.split(';') : [];
    cookies.forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      if (name) {
        document.cookie = name + '=;' + expired;
      }
    });
    this.cookiesItems.forEach(key => {
      document.cookie = key + '=;' + expired;
    });
    this.cookiesItems = [];
  }
}
